import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import {
  GET_TINYCMS_ROOT_FILES,
  RESTORE_TINYCMS_INTEGRATIONS,
  RESTORE_TINYCMS_ROOT_FILES,
} from '../../graphql';
import { useAccount } from '../../context/AccountContext';
import { localTimes } from './EventAudit';
import { askInk } from '../cms/InkConfirm';

/**
 * Site settings › History (UX optimization review, feedback ab8f2f84): every
 * change made on the Site settings page, by people and agents, newest first —
 * domains, Code & tracking, verification and ads.txt, licensed feed keys. A
 * verification/ads.txt or Code & tracking change can be restored from its
 * row. The look, navigation and pages go live with approval; their history is
 * in Content › Design, which the footer links to.
 */

export const SETTINGS_HISTORY_QUERY = 'GetTinycmsSettingsHistory';

const GET_TINYCMS_SETTINGS_HISTORY = gql`
  query GetTinycmsSettingsHistory($accountId: ID!) {
    tinycmsSettingsHistory(accountId: $accountId, limit: 50)
  }
`;

interface Entry {
  id: string;
  at: string;
  what: string;
  who: string;
  table: string;
  version: number | null;
}

const RESTORABLE: Record<string, { label: string; refetch: string }> = {
  tinycms_root_files: { label: 'verification and ads.txt', refetch: 'GetTinycmsRootFiles' },
  tinycms_integrations: { label: 'Code & tracking', refetch: 'GetTinycmsIntegrations' },
};

const SHOWN = 8;

const stamp = (iso: string) =>
  new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

export function SettingsHistory() {
  const { accountId } = useAccount();
  const { data, loading, error } = useQuery<{ tinycmsSettingsHistory: Entry[] }>(
    GET_TINYCMS_SETTINGS_HISTORY,
    { variables: { accountId }, skip: !accountId, fetchPolicy: 'cache-and-network' }
  );
  // Admins restore (the server checks too); the Verification section already asked.
  const { data: rootData } = useQuery(GET_TINYCMS_ROOT_FILES, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const canManage = Boolean(rootData?.tinycmsRootFiles?.canManage);
  const [restoreFiles, { loading: restoringFiles }] = useMutation(RESTORE_TINYCMS_ROOT_FILES);
  const [restoreCode, { loading: restoringCode }] = useMutation(RESTORE_TINYCMS_INTEGRATIONS);
  const [showAll, setShowAll] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const entries = data?.tinycmsSettingsHistory || [];
  // The newest saved version of each is what's live: nothing to restore there.
  const live = new Map<string, number>();
  for (const e of entries) {
    if (e.version != null && RESTORABLE[e.table] && !live.has(e.table))
      live.set(e.table, e.version);
  }
  const shown = showAll ? entries : entries.slice(0, SHOWN);

  const restore = async (e: Entry) => {
    const kind = RESTORABLE[e.table];
    if (!kind || e.version == null) return;
    if (
      !(await askInk({
        title: `Put ${kind.label} back as it was after this change (${stamp(e.at)})?`,
        body: 'It replaces what’s live now, and is saved as a new version.',
        confirmLabel: 'Restore',
      }))
    )
      return;
    setMessage(null);
    try {
      const options = {
        variables: { accountId, version: e.version },
        refetchQueries: [SETTINGS_HISTORY_QUERY, kind.refetch],
        awaitRefetchQueries: true,
      };
      if (e.table === 'tinycms_root_files') await restoreFiles(options);
      else await restoreCode(options);
      setMessage({ tone: 'ok', text: `Restored ${kind.label}. Live on your site now.` });
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  return (
    <div className="ink-site-card">
      {loading && !data ? (
        <p className="ink-site-hint" style={{ paddingTop: 14 }}>
          Loading…
        </p>
      ) : error && !data ? (
        <p className="ink-site-hint" style={{ paddingTop: 14 }} role="alert">
          The history couldn’t load just now.
        </p>
      ) : !entries.length ? (
        <p className="ink-site-hint" style={{ paddingTop: 14 }}>
          No changes on this page yet.
        </p>
      ) : (
        shown.map((e) => {
          const canRestore =
            canManage &&
            e.version != null &&
            Boolean(RESTORABLE[e.table]) &&
            live.get(e.table) !== e.version;
          return (
            <div key={e.id} className="ink-set-hist-row">
              <span className="ink-set-hist-when">{stamp(e.at)}</span>
              <span className="ink-set-hist-what">
                {localTimes(e.what)} <span className="ink-set-hist-who">· {e.who}</span>
              </span>
              {canRestore ? (
                <button
                  type="button"
                  className="ink-site-link ink-set-hist-restore"
                  disabled={restoringFiles || restoringCode}
                  onClick={() => void restore(e)}
                >
                  Restore this version
                </button>
              ) : (
                <span />
              )}
            </div>
          );
        })
      )}
      {entries.length > SHOWN ? (
        <div className="ink-site-hint">
          <button type="button" className="ink-site-link" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show fewer' : `Show all ${entries.length}`}
          </button>
        </div>
      ) : null}
      {message ? (
        <p
          className="ink-site-hint"
          role={message.tone === 'error' ? 'alert' : 'status'}
          style={{ color: message.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-green)' }}
        >
          {message.text}
        </p>
      ) : null}
      <p className="ink-site-hint ink-set-hist-foot">
        The look, navigation and pages go live with approval; their history is in{' '}
        <Link to="/content?view=manage&area=design" className="ink-site-link">
          Design
        </Link>
        .
      </p>
    </div>
  );
}
